// @flow

import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { compose } from "redux";
import { getCurrentPath } from "@selectors";
import { getUserId } from "@modules/account/selectors";
import { getRouteParams } from "@selectors";
import { route } from "@routes/helper";
import {
  isLoading,
  getActiveHierarchyTree,
  getSelectedBranchId,
  getNotificationContacts,
  getShiftPatterns,
  getSelectedBranch,
  isAddEditShiftPatternModalVisible,
  getAssignedShiftPattern,
  isAddEditContactModalVisible,
  getSelectedNotificationContact,
  hierarchyShiftPatternSchema,
} from "@modules/hierarchy/selectors";
import {
  Loader,
  TitleSection,
  Row,
  Col,
  Button,
  OrganizationHierarchyTree,
  View,
  Text,
  withFocusFirstInput,
} from "@components";
import type { State } from "@store/reducers";
import actionCreators from "@modules/hierarchy/actionCreator";
import employeesActionCreators from "@modules/employee/actionCreator";
import withErrorHandler from "@components/error";
import { withClientContext, type PropsFromClientContext } from "@modules/context/components/";
import {
  withPermissions,
  PermissionEnum,
  type PropsFromPermissions,
} from "@modules/account/components";
import {
  NotificationContactsTable,
  ShiftPatternsTable,
  OrganizationSummary,
  AddEditShiftPatternModal,
  AddEditContactModal,
} from "@modules/hierarchy/components";
import type {
  NotificationContact,
  AssignedShiftPattern,
} from "@modules/hierarchy/reducer/hierarchy/types.js";

type PropsFromState = $Exact<{
  isLoading: boolean,
  currentUserId: number | null,
  currentPath: string,
  selectedBranchId: number | null,
  activeHierarchyTree: ?Object,
  notificationContacts: NotificationContact[],
  shiftPatterns: AssignedShiftPattern[],
  selectedBranch: Object | null,
  isAddEditShiftPatternModalVisible: boolean,
  assignedShiftPattern: ?AssignedShiftPattern,
  isAddEditContactModalVisible: boolean,
  selectedNotificationContact: ?NotificationContact,
  hierarchyShiftPatternSchema: ?Object,
}>;

type PropsFromDispatch = $Exact<{
  GetActiveHierarchyTree: typeof actionCreators.GetActiveHierarchyTree,
  selectOrganization: typeof actionCreators.SelectOrganizationalUnit,
  getHierarchyNotificationsSummary: typeof actionCreators.GetHierarchyNotificationsSummary,
  toggleAddEditShiftPatternModal: typeof actionCreators.ToggleAddEditShiftPatternModal,
  toggleAddEditContactModal: typeof actionCreators.ToggleAddEditContactModal,
  setNotificationContact: typeof actionCreators.SetNotificationContact,
  callbacksEmployeeSearchResetResults: typeof actionCreators.CallbacksEmployeeSearchResetResults,
  createHierarchyNotification: typeof actionCreators.CreateHierarchyNotification,
  updateHierarchyNotification: typeof actionCreators.UpdateHierarchyNotification,
  getHierarchyShiftPatternSchema: typeof actionCreators.GetHierarchyShiftPatternSchema,
  createHierarchyShiftPattern: typeof actionCreators.CreateHierarchyShiftPattern,
  updateHierarchyShiftPattern: typeof actionCreators.UpdateHierarchyShiftPattern,
  deleteHierarchyNotification: typeof actionCreators.DeleteHierarchyNotification,
  deleteHierarchyShiftPatternAssignment: typeof actionCreators.DeleteHierarchyShiftPatternAssignment,
  exportFullList: typeof actionCreators.ExportFullList,
}>;

type Props = $Exact<{
  ...PropsFromPermissions,
  ...PropsFromClientContext,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

function DepartmentContacts(props: Props) {
  const [selectedBranch, setSelectedBranch] = useState();

  useEffect(() => {
    props.GetActiveHierarchyTree({
      clientId: props.clientId,
    });
    props.getHierarchyShiftPatternSchema({
      clientId: props.clientId,
    });
  }, [props.clientId]);

  useEffect(() => {
    if (!selectedBranch) preselectDefaultDepartment();
  }, [(props.activeHierarchyTree || {}).structure]);

  function openEmployeeProfile(employeeId: number) {
    window.open(
      route("REPORTING_EMPLOYEE_EMPLOYEE_ADMIN", { clientId: props.clientId, employeeId }),
      "_blank",
    );
  }

  function selectOrganization(branch: Object) {
    setSelectedBranch(branch);
    props.selectOrganization({
      clientId: props.clientId,
      branch,
    });
  }

  function preselectDefaultDepartment() {
    if (props.activeHierarchyTree && props.activeHierarchyTree.structure) {
      selectOrganization(props.activeHierarchyTree.structure);
    }
  }

  function handleBranchClick(branch: Object) {
    if (branch.data.employeeId) {
      openEmployeeProfile(branch.data.employeeId);
    } else {
      selectOrganization(branch);
    }
  }

  function onToggleAddEditShiftPatternModal(
    data: $Exact<{
      visible: boolean,
      assignedShiftPattern?: ?AssignedShiftPattern,
    }>,
  ) {
    return function() {
      props.toggleAddEditShiftPatternModal(data);
    };
  }

  function onToggleAddEditContactModal(
    data: $Exact<{
      visible: boolean,
      notificationContact?: NotificationContact,
    }>,
  ) {
    return function() {
      props.toggleAddEditContactModal(data);
      props.callbacksEmployeeSearchResetResults();
    };
  }

  function onAddEditContact(data: Object) {
    if ((props.selectedNotificationContact || {}).createNew) {
      props.createHierarchyNotification({
        clientId: props.clientId,
        organisationalUnitId: props.selectedBranchId,
        data: {
          organisationalUnit: {
            id: props.selectedBranchId,
          },
          ...data,
        },
      });
    } else if (props.selectedNotificationContact) {
      props.updateHierarchyNotification({
        clientId: props.clientId,
        organisationalUnitId: props.selectedBranchId,
        data: {
          organisationalUnit: {
            id: props.selectedBranchId,
          },
          id: props.selectedNotificationContact.id,
          ...data,
        },
      });
    } else {
      props.createHierarchyNotification({
        clientId: props.clientId,
        organisationalUnitId: props.selectedBranchId,
        data: {
          organisationalUnit: {
            id: props.selectedBranchId,
          },
          ...data,
        },
      });
    }
  }

  function onAddEditShiftPattern(data: Object) {
    if (props.assignedShiftPattern) {
      props.updateHierarchyShiftPattern({
        clientId: props.clientId,
        organisationalUnitId: props.selectedBranchId,
        data,
      });
    } else {
      props.createHierarchyShiftPattern({
        clientId: props.clientId,
        organisationalUnitId: props.selectedBranchId,
        data,
      });
    }
  }

  function onDeleteHierarchyNotification(notificationIds: number[]) {
    props.deleteHierarchyNotification({
      clientId: props.clientId,
      organisationalUnitId: props.selectedBranchId,
      notificationIds,
      successMessage: props.t(
        ["CONTACT_SUCCESSFUL_DELETED", "CONTACT_SUCCESSFUL_DELETED_plural", "count"],
        {
          count: notificationIds.length,
        },
      ),
    });
  }

  function onDeleteHierarchyShiftPatternAssignment(
    data: Array<
      $Exact<{
        assignmentRuleId: number,
        hierarchyUnassignShiftPatternDTO: $Exact<{
          isCascading: boolean,
        }>,
      }>,
    >,
  ) {
    props.deleteHierarchyShiftPatternAssignment({
      clientId: props.clientId,
      organisationalUnitId: props.selectedBranchId,
      data,
      successMessage: props.t(
        [
          "SHIFT_PATTERN_ASSIGNMENT_SUCCESSFUL_DELETED",
          "SHIFT_PATTERN_ASSIGNMENT_SUCCESSFUL_DELETED_plural",
          "count",
        ],
        {
          count: data.length,
        },
      ),
    });
  }

  function orderedNotificationContacts(contacts: *) {
    return contacts.sort((firstEl: Object, secondEl: Object) => {
      if (firstEl.recipient.lastName < secondEl.recipient.lastName) {
        return -1;
      } else if (firstEl.recipient.lastName > secondEl.recipient.lastName) {
        return 1;
      }
      return 0;
    });
  }

  function handleExportFullList() {
    props.exportFullList({
      clientId: props.clientId,
    });
  }

  return (
    <View>
      {props.isLoading && <Loader />}
      {props.isAddEditContactModalVisible && (
        <AddEditContactModal
          clientId={props.clientId}
          onSubmit={onAddEditContact}
          toggleModal={onToggleAddEditContactModal}
          selectedNotificationContact={props.selectedNotificationContact}
        />
      )}
      {props.isAddEditShiftPatternModalVisible && (
        <AddEditShiftPatternModal
          toggleModal={onToggleAddEditShiftPatternModal}
          assignedShiftPattern={props.assignedShiftPattern}
          schema={props.hierarchyShiftPatternSchema}
          onSubmit={onAddEditShiftPattern}
        />
      )}
      <View direction="row" gap={6} justifyContent="space-between">
        {props.activeHierarchyTree && (
          <View width="30%" className="gs-container">
            <OrganizationHierarchyTree
              tree={props.activeHierarchyTree.structure}
              disabled={true}
              searchable={true}
              expanded={[]}
              onDrop={null}
              onAction={null}
              selectedBranchId={props.selectedBranchId}
              onClick={handleBranchClick}
              searchPlaceholder={props.t("FIND_DEPARTMENT")}
              showDisabled={true}
              className="min-height-560-px"
              hideEmployee
            />
          </View>
        )}

        {props.selectedBranchId && (
          <View flex={1} gap={4}>
            <View direction="row" justifyContent="space-between" margin={[0, 0, 4, 0]}>
              <Text type="h5" text={selectedBranch?.data?.label || ""} />
              <Button
                materialIcon="file_download"
                onClick={handleExportFullList}
                label={props.t("EXPORT")}
              />
            </View>

            <View gap={4} className="gs-container">
              <View direction="row" justifyContent="space-between">
                <Text type="h6" text={props.t("NOTIFICATION_CONTACTS")} />
                <Button
                  materialIcon="add_box"
                  label={props.t("ADD")}
                  onClick={onToggleAddEditContactModal({ visible: true })}
                  size="small"
                />
              </View>
              <NotificationContactsTable
                notifications={orderedNotificationContacts(props.notificationContacts)}
                toggleModal={onToggleAddEditContactModal}
                deleteHierarchyNotification={onDeleteHierarchyNotification}
              />
            </View>

            <View gap={4} className="gs-container">
              <View direction="row" justifyContent="space-between">
                <Text type="h6" text={props.t("SHIFT_PATTERNS")} />{" "}
                <Button
                  materialIcon="add_box"
                  onClick={onToggleAddEditShiftPatternModal({
                    visible: true,
                  })}
                  label={props.t("ADD")}
                  size="small"
                />
              </View>
              <ShiftPatternsTable
                shiftPatterns={props.shiftPatterns}
                toggleModal={onToggleAddEditShiftPatternModal}
                deleteHierarchyShiftPatternAssignment={onDeleteHierarchyShiftPatternAssignment}
              />
            </View>
          </View>
        )}
      </View>
    </View>
  );
}

const mapStateToProps = (state: State, ownProps: Object): PropsFromState => {
  return {
    isLoading: isLoading(state),
    currentPath: getCurrentPath(state),
    currentUserId: getUserId(state),
    selectedBranchId: getSelectedBranchId(state),
    activeHierarchyTree: getActiveHierarchyTree(state),
    notificationContacts: getNotificationContacts(state),
    shiftPatterns: getShiftPatterns(state),
    selectedBranch: getSelectedBranch(state),
    isAddEditShiftPatternModalVisible: isAddEditShiftPatternModalVisible(state),
    assignedShiftPattern: getAssignedShiftPattern(state),
    isAddEditContactModalVisible: isAddEditContactModalVisible(state),
    selectedNotificationContact: getSelectedNotificationContact(state),
    hierarchyShiftPatternSchema: hierarchyShiftPatternSchema(state),
  };
};

const mapDispatchFromProps: PropsFromDispatch = {
  GetActiveHierarchyTree: actionCreators.GetActiveHierarchyTree,
  selectOrganization: actionCreators.SelectOrganizationalUnit,
  getHierarchyNotificationsSummary: actionCreators.GetHierarchyNotificationsSummary,
  toggleAddEditShiftPatternModal: actionCreators.ToggleAddEditShiftPatternModal,
  toggleAddEditContactModal: actionCreators.ToggleAddEditContactModal,
  setNotificationContact: actionCreators.SetNotificationContact,
  callbacksEmployeeSearchResetResults: actionCreators.CallbacksEmployeeSearchResetResults,
  createHierarchyNotification: actionCreators.CreateHierarchyNotification,
  updateHierarchyNotification: actionCreators.UpdateHierarchyNotification,
  getHierarchyShiftPatternSchema: actionCreators.GetHierarchyShiftPatternSchema,
  createHierarchyShiftPattern: actionCreators.CreateHierarchyShiftPattern,
  updateHierarchyShiftPattern: actionCreators.UpdateHierarchyShiftPattern,
  deleteHierarchyNotification: actionCreators.DeleteHierarchyNotification,
  deleteHierarchyShiftPatternAssignment: actionCreators.DeleteHierarchyShiftPatternAssignment,
  exportFullList: actionCreators.ExportFullList,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchFromProps),
)(withFocusFirstInput(DepartmentContacts));
